import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { DateTime } from "luxon";
import { cancel, stampComplete, balanceView, setDirection } from "../actions";
import { ConfirmMultiStampProps, CostDivisionListProps, DeviationListProps, SelectDirectionProps, ButtonTypes, StampDirection, StampOperation, EnrollmentProps, AuthenticatedPerson } from "../models";
import { Session } from "../reducers/session";
import { reload } from "../../../site-mobileweb/home/actions";

enum StampFormPage {
    None,
    SelectDirection,
    Enrollment,
    DeviationList,
    CostDivisionList,
    ConfirmMultistamp,
}

interface StampFormState {
    selectedCostDivision?: number;
    multiConfirmation?: boolean;
    previousPage: StampFormPage;
    currentPage: StampFormPage;
}

function mapStateToProps(state: { session: Session }) {
    return {
        person: { ...state.session.stampPerson },
        direction: state.session.stampDirection,
        apiLoading: state.session.apiLoading,
        apiOfflineApp: state.session.apiOfflineApp
    };
}

const connector = connect(mapStateToProps);

export interface StampFormOwnProps {
    onStamp: () => unknown
    confirmMultiStampComponent: React.ComponentType<ConfirmMultiStampProps>
    costDivisionListComponent: React.ComponentType<CostDivisionListProps>
    deviationListComponent: React.ComponentType<DeviationListProps>
    enrollmentComponent: React.ComponentType<EnrollmentProps>
    selectDirectionComponent: React.ComponentType<SelectDirectionProps>
}

type StampFormProps = StampFormOwnProps & ConnectedProps<typeof connector>;

class _StampForm extends React.Component<StampFormProps, StampFormState> {
    constructor(props: StampFormProps) {
        super(props);
        this.state = {
            previousPage: StampFormPage.None,
            currentPage: StampFormPage.SelectDirection,
            selectedCostDivision: null,
        };
    }

    render() {
        const { person } = this.props;

        switch (this.state.currentPage) {
            case StampFormPage.ConfirmMultistamp: {
                return (
                    <this.props.confirmMultiStampComponent
                        stampDirection={this.props.direction}
                        onClickHandler={this.handleMultiStamp} />
                );
            }
            case StampFormPage.CostDivisionList: {
                return (
                    <this.props.costDivisionListComponent
                        task={person.task}
                        lastStamp={person.lastStamp}
                        costDivisions={person.stampCostDivisions}
                        onSelect={this.handleCostDivision}
                        onBackClick={this.handleBackClick} />
                );
            }
            case StampFormPage.DeviationList: {
                return (
                    <this.props.deviationListComponent
                        deviations={person.stampOperations}
                        lastStamp={person.lastStamp}
                        stampInStatus={person.stampInStatus}
                        stampOutStatus={person.stampOutStatus}
                        task={person.task}
                        direction={this.props.direction}
                        onClickHandler={this.handleDeviationCode}
                        onBackClick={this.handleBackClick}
                        apiLoading={this.props.apiLoading} />
                );
            }
            case StampFormPage.Enrollment: {
                return <this.props.enrollmentComponent fakeFinger={true} enrollmentMeta="RightThumb" />;
            }
            case StampFormPage.SelectDirection: {
                return (
                    <this.props.selectDirectionComponent
                        person={person}
                        cancelClick={this.handleCancelClick}
                        onClick={this.handleTimeStampClick} />
                );
            }
        }
    }

    getPageToRender(): StampFormPage {
        const { person } = this.props;
        const hasStampDirection = this.props.direction !== StampDirection.None;

        if (hasStampDirection && this.state.multiConfirmation === false) {
            return StampFormPage.ConfirmMultistamp;
        } else if (this.shallCostDivisionViewBeShown(this.props.direction)) {
            return StampFormPage.CostDivisionList;
        } else if (hasStampDirection && this.props.direction && this.shallDeviationCodeViewBeShown(this.props.direction)) {
            return StampFormPage.DeviationList;
        } else if (person.demandFingerprint) {
            return StampFormPage.Enrollment;
        } else {
            return StampFormPage.SelectDirection;
        }
    }

    componentDidUpdate(): void {
        if (!this.props.apiLoading) {
            const pageToRender = this.getPageToRender();
            if (pageToRender !== this.state.currentPage) {
                this.setState({
                    previousPage: this.state.currentPage,
                    currentPage: pageToRender,
                });
            }
        }
    }

    shallCostDivisionViewBeShown(direction: StampDirection) {
        const { person } = this.props;

        const inCurrentSiteWithoutTask = direction === StampDirection.In && !person.task;
        const inOtherSiteTask = direction === StampDirection.In && person.task && !person.task.stampTarget;

        return (inCurrentSiteWithoutTask || inOtherSiteTask || direction === StampDirection.Change) &&
            this.state.selectedCostDivision === null &&
            person.stampCostDivisions &&
            !this.isOfflineStamp() &&
            !this.personHasOnlyOneCostDivision();
    }

    isOfflineStamp() {
        const { person } = this.props;
        return !person.stampInStatus && !person.stampOutStatus;
    }

    personHasOnlyOneCostDivision() {
        return this.props.person.stampCostDivisions && this.props.person.stampCostDivisions.length === 1;
    }

    handleCancelClick = () => this.props.dispatch(cancel());

    shallDeviationCodeViewBeShown(stampDirection: StampDirection): boolean {
        const { stampOperations } = this.props.person;

        const multipleOrNoOperations = stampOperations.filter(so => StampDirection[so.direction] === stampDirection).length != 1;

        if (stampDirection === StampDirection.In || stampDirection === StampDirection.Out) {
            return !this.isOfflineStamp() && multipleOrNoOperations;
        } else if (stampDirection === StampDirection.Change) {
            return false;
        }
    }

    handleTimeStampClick = (direction: StampDirection) => {
        const { person, dispatch, apiOfflineApp } = this.props;
        if (apiOfflineApp) dispatch(reload());

        const isMultiStamp =
            person.lastStamp &&
            person.lastStamp.dateTime.hasSame(DateTime.now(), "day") &&
            direction !== StampDirection.Change &&
            StampDirection[person.lastStamp.direction] === direction;

        const multiConfirmation = isMultiStamp ? false : null;

        dispatch(setDirection(direction));
        this.setState({
            selectedCostDivision: person.stampCostDivisions && this.personHasOnlyOneCostDivision() ? person.stampCostDivisions[0].costDivisionRef : null,
            multiConfirmation
        });

        if (multiConfirmation !== null ||
            this.shallDeviationCodeViewBeShown(direction) ||
            this.shallCostDivisionViewBeShown(direction) ||
            direction === StampDirection.Change) {
            return;
        } else if (direction == StampDirection.Balance) {
            this.getUserBalance(person, direction, dispatch);
        } else {
            const stampOp = person.stampOperations.filter((so) => StampDirection[so.direction] == direction)[0];

            this.saveStamp(stampOp?.deviationCodeRef, stampOp?.date, direction, this.getCostDivisionToStamp());
        }
    };

    handleDeviationCode = (stampOp: StampOperation) => {
        this.saveStamp(stampOp.deviationCodeRef, stampOp.date, this.props.direction, this.getCostDivisionToStamp());
    };

    handleCostDivision = (costDivisionRef: number) => {
        if (this.shallDeviationCodeViewBeShown(this.props.direction)) {
            this.setState({ selectedCostDivision: costDivisionRef });
        } else {
            const stampOp = this.props.person.stampOperations
                .filter((so) => StampDirection[so.direction] === this.props.direction)[0];

            this.saveStamp(stampOp.deviationCodeRef, stampOp.date, this.props.direction, costDivisionRef);
        }
    };

    handleMultiStamp = (value) => {
        if (this.props.apiLoading) {
            return;
        }

        if (value === ButtonTypes.Yes &&
            (this.shallDeviationCodeViewBeShown(this.props.direction) || this.shallCostDivisionViewBeShown(this.props.direction))) {
            this.setState({
                multiConfirmation: true
            });
        } else if (value === ButtonTypes.No) {
            this.showDefault();
        } else {
            const stampOp = this.props.person.stampOperations
                .filter((so) => StampDirection[so.direction] === this.props.direction)[0];
            this.saveStamp(stampOp.deviationCodeRef, stampOp.date, this.props.direction, this.getCostDivisionToStamp());
        }
    };

    getCostDivisionToStamp() {
        const { person } = this.props;

        if (person.task && person.task.stampTarget) {
            return person.task.costDivisionRef;
        } else if (this.state.selectedCostDivision) {
            return this.state.selectedCostDivision;
        } else if (this.personHasOnlyOneCostDivision()) {
            return person.stampCostDivisions[0].costDivisionRef;
        }

        return null;
    }

    getUserBalance = (person: AuthenticatedPerson, direction, dispatch) => {
        dispatch(balanceView(person, StampDirection[direction]));
    };

    async saveStamp(deviationCodeRef, stampDate, direction: StampDirection, selectedCostDivision) {
        const { dispatch, person, onStamp } = this.props;

        if (this.props.apiLoading) {
            return;
        }

        const isInChangeStamp = direction === StampDirection.In && person.task && !person.task.stampTarget;

        if (isInChangeStamp) {
            await dispatch(stampComplete(person, StampDirection.In, deviationCodeRef, stampDate, null, onStamp));
            await dispatch(stampComplete(person, StampDirection.Change, null, stampDate, selectedCostDivision, onStamp));
        } else {
            await dispatch(stampComplete(person, direction, deviationCodeRef, stampDate, selectedCostDivision, onStamp));
        }
    }

    handleBackClick = (): void => {
        if (this.state.currentPage === StampFormPage.CostDivisionList) {
            this.showPage(StampFormPage.SelectDirection);
        } else if (this.state.currentPage === StampFormPage.DeviationList) {
            if (this.state.previousPage === StampFormPage.CostDivisionList) {
                this.showPage(StampFormPage.CostDivisionList);
            } else {
                this.showPage(StampFormPage.SelectDirection);
            }
        }
    };

    showPage = (pageToShow: StampFormPage): void => {
        switch (pageToShow) {
            case StampFormPage.None:
            case StampFormPage.SelectDirection: {
                this.showDefault();
                break;
            }
            case StampFormPage.CostDivisionList: {
                this.showCostDivision();
                break;
            }
        }
    };

    showDefault = (): void => {
        const { dispatch } = this.props;

        this.setState({
            multiConfirmation: null,
            selectedCostDivision: null
        });
        dispatch(setDirection(StampDirection.None));
    };

    showCostDivision = () => {
        this.setState({
            selectedCostDivision: null
        });
    };
}

export const StampForm = connector(_StampForm);
